import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";

const ArticleCard = ({ article }) => {
  return (
    <div className="card border-t-4 border-t-blue-500 shadow-lg">
      <div className="p-6 flex flex-col h-full justify-between">
        <div>
          <h3 className="text-xl font-semibold text-white min-h-[50px]">
            {article.title}
          </h3>
          <p className="mt-2 text-gray-400">{article.description}</p>
        </div>
        <Link
          to={`/article?file=${article.file}`}
          className="mt-4 inline-block px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition text-center"
        >
          Read more &rarr;
        </Link>
      </div>
    </div>
  );
};

const educationalArticles = [
  {
    title: "Best Practices for Flaky Test Triage",
    description:
      "Learn how to identify and fix unreliable tests that waste your team's time.",
    topic: "test-automation",
    file: "flaky-tests.md",
  },
  {
    title: "How GenAI is Changing the CI/CD Pipeline",
    description:
      "A deep dive into how AI is automating test generation and analysis for faster releases.",
    topic: "genai-in-testing",
    file: "genai-cicd.md",
  },
  {
    title: "Beginner's Guide to Playwright with TypeScript",
    description:
      "Get started with your first end-to-end test in under 15 minutes.",
    topic: "playwright",
    file: "playwright-guide.md",
  },
  {
    title: "Setting up CI/CD Pipelines for Test Automation",
    description:
      "Learn to integrate your tests with Jenkins to automate your release cycle.",
    topic: "ci-cd",
    file: "ci-cd-pipelines.md",
  },
  {
    title: "Advanced Page Object Model with Playwright",
    description:
      "Explore best practices for organizing large test suites using the Page Object Model.",
    topic: "playwright",
    file: "playwright-ts-2.md",
  },
  {
    title: "Mastering Data-Driven Testing",
    description:
      "A step-by-step guide to writing robust tests using external data sources.",
    topic: "data-driven-testing",
    file: "data-driven-testing.md",
  },
  {
    title: "Getting Started with Cypress",
    description:
      "An introduction to the fundamentals and advanced features of the Cypress framework.",
    topic: "cypress",
    file: "cypress-intro.md",
  },
  {
    title: "Using AI for Test Case Prioritization",
    description:
      "A case study on how AI can help you run the most impactful tests first.",
    topic: "genai-in-testing",
    file: "ai-case-study.md",
  },
];

const EducationalTopics = () => {
  const [articles, setArticles] = useState([]);
  const [topicTitle, setTopicTitle] = useState("");
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const topic = params.get("topic");

    const capitalizeWords = (str) => {
      if (!str) return "";
      return str
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    if (topic) {
      const filteredArticles = educationalArticles.filter(
        (article) => article.topic === topic
      );
      setArticles(filteredArticles);

      // Set appropriate title based on topic
      if (topic === "ci-cd") {
        setTopicTitle("CI/CD Pipelines");
      } else if (topic === "genai-in-testing") {
        setTopicTitle("GenAI in Testing");
      } else {
        setTopicTitle(capitalizeWords(topic));
      }
    } else {
      setArticles(educationalArticles);
      setTopicTitle("All Educational Content");
    }
  }, [location.search]);

  return (
    <main className="container mx-auto px-6 py-12">
      <section className="py-16 md:py-24">
        <div className="text-center mb-12">
          <Link
            to="/"
            className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-6"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Home
          </Link>
          <h2 className="text-4xl font-bold text-white mb-4">{topicTitle}</h2>
          <p className="text-gray-400 text-lg">
            Explore our comprehensive collection of educational content
          </p>
        </div>

        {articles.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <ArticleCard key={index} article={article} />
            ))}
          </div>
        ) : (
          <div className="text-center">
            <p className="text-gray-400">
              There are no articles for this topic yet. Please check back later!
            </p>
          </div>
        )}
      </section>
    </main>
  );
};

export default EducationalTopics;
